<template>
  <div class="component-roomtypes">
    <b-modal
      id="projectKeyModal"
      ref="projectKeyModal"
      no-fade
      centered
      static
      size="lg"
      modal-class="form-modal"
      :ok-title="$t(`buttons.${projectKey.id != null ? 'update' : 'save'}`)"
      ok-variant="primary"
      :cancel-title="$t('buttons.cancel')"
      cancel-variant="outline-primary"
      :ok-disabled="updatePending || !$refs.projectKeyForm || !formValid || !$refs.projectKeyForm.flags.valid"
      :cancel-disabled="updatePending"
      :no-close-on-esc="updatePending"
      :no-close-on-backdrop="updatePending"
      :hide-header-close="updatePending"
      @show="modalScroll"
      @hidden="modalScroll"
      @ok.prevent="processForm"
    >
      <template #modal-header-close>
        <icon width="20" height="20" class="d-none d-md-block" type="times"/>
        <icon width="10" height="18" class="d-md-none" type="arrow-left"/>
      </template>
      <template #modal-title>
        {{ modalTitle }}
      </template>
      <ValidationObserver ref="projectKeyForm" slim>
        <div class="position-relative-w-100">
          <p class="txt-red">
            {{ $t('pages.project-key.rules.camel-case') }}
            <span class="bold txt-bold">{{ $t('pages.project-key.rules.camel-case-eg') }}</span>
          </p>
          <p class="txt-red">
            {{ $t('pages.project-key.rules.variables') }}
            <span class="bold txt-bold">{{ $t('pages.project-key.rules.variables-eg') }}</span>
          </p>
          <p class="txt-red">
            {{ $t('pages.project-key.rules.placeholder-variable') }}
            <span class="bold txt-bold">{{ $t('pages.project-key.rules.placeholder-variable-eg') }}</span>
          </p>
        </div>
        <div class="edge edge-amount" >
          <h3 :content="$t('pages.project-key.modal.section-key')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="text" id="key" name="key" class="mb-0" no-icon no-tooltip
                v-model="projectKey.key"
                :disabled="updatePending"
                ref="fieldAmount"
                :rules="rulesFor('key')"
              />
            </div>
          </div>
        </div>
        <div class="edge edge-amount" >
          <h3 :content="$t('pages.project-key.modal.english-translation-section')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="text" id="key" name="key" class="mb-0" no-icon no-tooltip
                v-model="projectKey.englishVersion"
                :disabled="updatePending"
                ref="fieldAmount"
                :rules="rulesFor('englishVersion')"
              />
            </div>
          </div>
        </div>
        <div class="edge edge-amount">
          <h3 :content="$t('pages.project-key.modal.section-description')"></h3>
          <div class="row row-edit">
            <div class="col">
              <ValidatedField
                type="text" id="description" name="description" class="mb-0" no-icon no-tooltip
                v-model="projectKey.description"
                :disabled="updatePending"
                ref="fieldAmount"
                :rules="rulesFor('description')"
              />
            </div>
          </div>
        </div>
      </ValidationObserver>
    </b-modal>

    <div class="panel position-relative panel-content">
      <Breadcrumb :items=breadCrumbs() v-if="loaded"/>
      <div class="list d-none d-md-block left-edge">
        <p class="head-line justify-content-between">
          <span>{{ $t('pages.project-key.title') }}</span>
          <search-filter
            v-model="filter"
            :disabled="!loaded || pending"
            :placeholder="$t('pages.project-key.filter-tip')"
            @input="onSearch()"
          />
          <spinner v-if="!loaded" /></p>
        <div class="position-relative-w-100">
          <p class="txt-red">
            {{ $t('pages.project-key.rules.camel-case') }}
            <span class="bold txt-bold">{{ $t('pages.project-key.rules.camel-case-eg') }}</span>
          </p>
          <p class="txt-red">
            {{ $t('pages.project-key.rules.variables') }}
            <span class="bold txt-bold">{{ $t('pages.project-key.rules.variables-eg') }}</span>
          </p>
          <p class="txt-red">
            {{ $t('pages.project-key.rules.placeholder-variable') }}
            <span class="bold txt-bold">{{ $t('pages.project-key.rules.placeholder-variable-eg') }}</span>
          </p>
        </div>
        <div class="plans-table" v-if="loaded">
          <table class="w-100">
            <thead>
              <tr>
                <th class="w-id">{{ $t('id') }}</th>
                <th class="w-name">{{ $t('pages.project-key.headers.key') }}</th>
                <th class="w-name">{{ $t('pages.project-key.headers.english-version') }}</th>
                <th class="w-occupancy text-center">{{ $t('pages.project-key.headers.description') }}</th>
                <th class="w-actions">{{ $t('actions') }}</th>
              </tr>
            </thead>

            <tbody v-if="projectKeys.length === 0">
              <tr>
                <td colspan="5" class="w-empty">{{ $t('pages.project-key.no-project-key') }}</td>
              </tr>
            </tbody>
            <tbody v-for="row in projectKeys" :key="row.id">
              <tr class="separator before"></tr>
              <tr>
                <td>
                  {{ row.id }}
                </td>
                <td>
                  <p>{{ row.key }}</p>
                </td>
                <td>
                  {{ row.englishVersion }}
                </td>
                <td class="text-center">
                  {{ row.description }}
                </td>
                <td class="actions">
                  <b-btn class="btn-icon btn-tiny" @click="editProjectKey(row)" :disabled="updatePending">
                    <icon width="17" height="17" type="edit"/>
                  </b-btn>
                  <b-btn class="btn-icon btn-tiny" @click="removeProjectKey(row.id)">
                    <icon width="19" height="19" type="delete"/>
                  </b-btn>
                </td>
              </tr>
              <tr class="separator after"></tr>
            </tbody>
          </table>
        </div>
        <div v-if="loaded">
          <b-btn pill variant="outline-primary" class="add-new-plan"
                 @click="openCreateForm">
            <icon width="10" height="11" type="plus"/>
            {{ $t('pages.project-key.button-add') }}
          </b-btn>
        </div>
        <pagination
          v-if="loaded && totalCount && setPagination"
          :perPage="20"
          :currentPage="currentPage"
          :totalRows="totalCount"
          @update:current-page="updateCurrentPage($event)"
        />
      </div>
    </div>
    <div class="d-md-none add-new-plan-block" v-if="loaded">
      <b-btn pill variant="outline-primary" class="add-new-plan" @click="openCreateForm">
        <icon width="10" height="10" type="plus"/>
        {{ $t('pages.project-key.button-add') }}
      </b-btn>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex';
  import Swal from 'sweetalert2';

  export default {
    name: 'ProjectKeys',
    props: {
      payload: {
        validator: (v) => ['object', 'undefined'].includes(typeof v),
        default: null,
      },
    },
    data: () => ({
      projectKey: {},
      currentPage: 1,
      filter: '',
      setPagination: true,
    }),
    async created() {
      this.resetProjectKeyModal();
      await this.show();
    },
    computed: {
      ...mapGetters('projectKeys', ['loaded', 'noProjectKeys', 'projectKeys']),
      ...mapState('projectKeys', ['error', 'pending', 'updatePending', 'totalCount', 'projectPage', 'project']),
      formValid() {
        return true;
      },
      modalTitle() {
        if (this.projectKey.id == null) {
          return this.$t('pages.project-key.modal.title-add');
        }
        const { id, name } = this.projectKey;
        return this.$t('pages.project-key.modal.title-edit', { id, name });
      },
    },
    methods: {
      ...mapActions('projectKeys', ['fetchData', 'createProjectKey', 'updateProjectKey', 'deleteProjectKey']),

      modalScroll(ev) {
        const modal = ev.target;
        this.$nextTick(() => {
          if (modal != null) modal.scrollTop = 0;
        });
      },
      resetProjectKeyModal() {
        this.projectKey = {
          key: '',
          description: '',
          project_page_id: '',
          englishVersion: '',
        };
        if (this.$refs.projectKeyForm != null) {
          this.$refs.projectKeyForm.reset();
        }
      },
      openCreateForm() {
        this.resetProjectKeyModal();
        this.$nextTick(this.$refs.projectKeyModal.show);
      },
      editProjectKey(projectKey) {
        this.projectKey = {};
        this.resetProjectKeyModal();
        this.projectKey = {
          ...this.projectKey,
          ...JSON.parse(JSON.stringify(projectKey)), // deep clone without bindings
        };
        this.$nextTick(() => {
          this.$refs.projectKeyForm.reset();
          this.$refs.projectKeyModal.show();
        });
      },
      async processForm() {
        const projectKey = { ...this.projectKey };
        if (this.payload != null) {
          Object.keys(this.payload).forEach((k) => {
            projectKey[k] = this.payload[k];
          });
        }
        try {
          projectKey.project_page_id = this.$route.params.id;
          if (projectKey.id != null) {
            await this.updateProjectKey(projectKey);
          } else {
            await this.createProjectKey(projectKey);
          }
          this.$refs.projectKeyModal.hide();
          this.$toastr.s({
            title: projectKey.id ? this.$t('pages.project-key.alert-edit') : this.$t('pages.project-key.alert-saved'),
            msg: '',
            timeout: 3000,
            progressbar: false,
          });
          await this.show();
        } catch (error) {
          this.$toastr.e(error.message, this.$t('error'));
        }
      },
      async show() {
        await this.fetchData({
          currentPage: this.currentPage,
          projectPageId: this.$route.params.id,
          q: this.filter,
        });
      },
      rulesFor(field) {
        const rules = {
          required: true,
        };
        switch (field) {
          case 'key':
            break;
          case 'description':
            break;
          case 'englishVersion':
            break;
          default:
            break;
        }
        return rules;
      },
      updateCurrentPage(currentPage) {
        this.currentPage = currentPage;
        this.show();
      },
      breadCrumbs() {
        return [
          {
            text: this.$t('pages.dashboard.title'),
            to: { name: 'dashboard' },
          },
          {
            text: this.project.name,
            to: { name: 'projects' },
          },
          {
            text: this.projectPage.name,
            to: { name: 'projects-pages', params: { id: this.project.id } },
          },
          {
            text: this.$t('pages.project-key.title'),
            active: true,
          },
        ];
      },
      async removeProjectKey(kid) {
        const result = await Swal.fire({
          position: 'top',
          text: 'Are you sure to delete this project page key ?',
          icon: 'warning',
          showCancelButton: true,
          cancelButtonColor: '#d33',
          confirmButtonColor: 'rgb(74 144 226)',
        });
        if (result.isConfirmed) {
          await this.deleteProjectKey(kid);
          this.show();
          Swal.fire({
            position: 'top',
            title: 'Deleted',
            text: 'Page Key deleted',
            icon: 'success',
            showConfirmButton: false,
            timer: 1000,
          });
        }
      },
      async onSearch() {
        if (this.filter.length === 0 || this.filter.length >= 3) {
          if (this.filter.length >= 3) {
            this.setPagination = false;
          } else {
            this.setPagination = true;
          }
          await this.show();
        }
      },
    },
  };
</script>
